/**
 * Ventana de Mensajes de Órdenes — interruptor "Seguimiento Asistente" (2026-10-08): textos,
 * fecha corta, marca "Respondió el …" y validación de la nota. Puro: se prueba desde
 * scripts/assistant-watch-view.test.ts.
 */

/** Lo que devuelve GET/PUT `/mercadolibre/messages/conversations/:packId/assistant-watch`. */
export interface AssistantWatch {
  enabled: boolean;
  note?: string | null;
  last_buyer_message_at?: string | null;
  acknowledged_at?: string | null;
  case_id?: number | null;
}

export const ASSISTANT_WATCH_LABEL = 'Seguimiento Asistente';
export const ASSISTANT_WATCH_HELP =
  'Cuando el comprador escriba, el asistente lo anota y te lo recuerda en la charla y en la Bandeja.';
export const ASSISTANT_WATCH_NOTE_PLACEHOLDER = '¿Qué esperás del comprador?';
export const ASSISTANT_WATCH_NOTE_MAX = 300;
/** `id` estable del interruptor (accesibilidad y pruebas). */
export const ASSISTANT_WATCH_TOGGLE_ID = 'assistant-watch-toggle';
export const ASSISTANT_WATCH_NOTE_ID = 'assistant-watch-note';

/**
 * Solo en la ventana que se abre desde Órdenes (rutas por defecto). El pop-up del depósito usa
 * otras rutas y no lo muestra, salvo que se pida explícitamente con `allow`.
 */
export function showAssistantWatch(
  basePath: string,
  defaultBase: string,
  allow?: boolean
): boolean {
  if (allow !== undefined) return allow;
  return basePath === defaultBase;
}

const time = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/** "8/10" (día/mes, hora local de quien mira). Vacío si la fecha no sirve. */
export function shortDate(iso: string | null | undefined): string {
  const t = time(iso);
  if (t === null) return '';
  const d = new Date(t);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

/** ¿El comprador respondió y todavía no se vio? */
export function hasUnseenReply(w: AssistantWatch | null | undefined): boolean {
  if (!w?.enabled) return false;
  const last = time(w.last_buyer_message_at);
  if (last === null) return false;
  const ack = time(w.acknowledged_at);
  return ack === null || last > ack;
}

/** Marca junto al interruptor: "Respondió el 8/10", o null si no hay novedad. */
export function repliedBadge(w: AssistantWatch | null | undefined): string | null {
  if (!hasUnseenReply(w)) return null;
  const d = shortDate(w!.last_buyer_message_at);
  return d ? `Respondió el ${d}` : 'Respondió';
}

/** Nota limpia (null = sin nota) o el motivo para no guardarla. */
export function validateWatchNote(
  text: string
): { ok: true; value: string | null } | { ok: false; error: string } {
  const v = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (v.length > ASSISTANT_WATCH_NOTE_MAX)
    return { ok: false, error: `La nota no puede pasar de ${ASSISTANT_WATCH_NOTE_MAX} letras` };
  return { ok: true, value: v || null };
}

export const ASSISTANT_WATCH_FORBIDDEN = 'No tenés permiso para esto';
export const ASSISTANT_WATCH_SAVE_ERROR = 'No se pudo guardar el seguimiento; probá de nuevo';

/** ¿La respuesta es un "sin permiso" del servidor (403 → `error: 'forbidden'`)? */
export function isForbidden(res: unknown): boolean {
  const r = res as { status?: unknown; error?: unknown } | null | undefined;
  return r?.status === 403 || String(r?.error ?? '').toLowerCase() === 'forbidden';
}

/**
 * Texto para el dueño cuando falla leer o guardar la vigilancia: nunca el texto crudo del
 * servidor (puede venir en inglés).
 */
export function watchErrorText(res: unknown): string {
  return isForbidden(res) ? ASSISTANT_WATCH_FORBIDDEN : ASSISTANT_WATCH_SAVE_ERROR;
}
